import React from 'react';
import {
  SparklesIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  BoltIcon
} from '@heroicons/react/24/outline';
import { KPIItem } from '../ui/KPIStripLayout';

const ModernPageHeader = ({ 
  title, 
  subtitle, 
  icon: Icon, 
  gradient = 'blue',
  stats = [],
  actions = [],
  variant = 'default',
  compact = false,
  eyebrow,
  artSrc,
  artAlt = '',
  // Used when artSrc 404s — lets a per-brand asset be requested before it exists,
  // falling back to the shared image instead of showing a broken-image icon.
  artFallbackSrc = null,
  className = ''
}) => {
  const gradientClasses = {
    blue: 'from-blue-600 via-blue-500 to-indigo-600',
    green: 'from-green-600 via-emerald-500 to-teal-600',
    purple: 'from-purple-600 via-violet-500 to-indigo-600',
    orange: 'from-orange-600 via-amber-500 to-yellow-600',
    red: 'from-red-600 via-rose-500 to-pink-600',
    indigo: 'from-indigo-600 via-blue-500 to-purple-600',
    gray: 'from-gray-700 via-gray-600 to-slate-700',
    // FieldJobView asked for 'amber' and silently fell back to the default, so that page has
    // never rendered the colour it requests. Added rather than changed at the call site
    // because 'amber' is the honest name for what that page wants.
    amber: 'from-amber-600 via-amber-500 to-orange-600'
  };

  const iconBgClasses = {
    blue: 'from-blue-500 to-indigo-600',
    green: 'from-green-500 to-emerald-600',
    purple: 'from-purple-500 to-violet-600',
    orange: 'from-orange-500 to-amber-600',
    red: 'from-red-500 to-rose-600',
    indigo: 'from-indigo-500 to-blue-600',
    amber: 'from-amber-500 to-orange-600',
    gray: 'from-gray-600 to-slate-700'
  };

  // Soft icon chips for the COMPACT header. A saturated gradient square with a white glyph
  // is a hero-card treatment: beside flat text on a flat background it reads as a leftover
  // from the banner rather than part of the title, which is what made the first compact
  // pass look "off" while being otherwise plainer. A tinted chip with a coloured glyph is
  // the same identity at the right volume, and it is what Linear, Notion and Stripe use in
  // their title bars.
  const iconSoftClasses = {
    blue: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
    green: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    purple: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
    orange: 'bg-orange-500/10 text-orange-600 dark:text-orange-400',
    red: 'bg-red-500/10 text-red-600 dark:text-red-400',
    indigo: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400',
    amber: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
    gray: 'bg-slate-500/10 text-slate-600 dark:text-slate-300'
  };

  // Swap to the fallback once, then stop. Clearing onError first is what prevents an
  // infinite request loop when the fallback is missing too.
  const handleArtError = (e) => {
    if (!artFallbackSrc || e.target.dataset.fallbackApplied) return;
    e.target.dataset.fallbackApplied = 'true';
    e.target.onerror = null;
    e.target.src = artFallbackSrc;
  };

  if (variant === 'business-dashboard') {
    return (
      <section className={`business-dashboard-header ${className}`}>
        <div className="business-dashboard-header__grid" aria-hidden="true"></div>
        <div className="business-dashboard-header__blueprint" aria-hidden="true"></div>

        <div className="business-dashboard-header__content">
          {artSrc && (
            <div className="business-dashboard-header__art" aria-hidden={artAlt ? undefined : 'true'}>
              <img src={artSrc} alt={artAlt} onError={handleArtError} />
            </div>
          )}

          <div className="business-dashboard-header__copy">
            {eyebrow && <p className="business-dashboard-header__eyebrow">{eyebrow}</p>}
            <h1>{title}</h1>
            {subtitle && <p className="business-dashboard-header__subtitle">{subtitle}</p>}

            {stats.length > 0 && (
              <div className="business-dashboard-header__stats">
                {/* A stat with nothing to click is a NUMBER, not a broken control.
                    This rendered every stat as <button disabled={!stat.onClick}>, so a plain
                    count like Customers' "Total: 86" became a genuinely disabled button in the
                    DOM: skipped in the tab order and announced to a screen reader as a control
                    you are not allowed to use. It reads as plain text to a sighted user, which
                    is why it kept being reported by machines and kept failing to reproduce by
                    eye — both observations were correct about different things.
                    Same shape OperationalMetricStrip already uses: element by capability. */}
                {stats.map((stat, index) => {
                  const StatElement = stat.onClick ? 'button' : 'div';
                  return (
                    <StatElement
                      key={`${stat.label}-${index}`}
                      type={stat.onClick ? 'button' : undefined}
                      onClick={stat.onClick}
                      className="business-dashboard-header__stat"
                    >
                      <span className="business-dashboard-header__stat-dot" aria-hidden="true"></span>
                      <span>{stat.label}</span>
                      <strong>{stat.value}</strong>
                    </StatElement>
                  );
                })}
              </div>
            )}
          </div>

          {actions.length > 0 && (
            <div className="business-dashboard-header__actions">
              {actions.map((action, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={action.onClick}
                  data-testid={action.testId}
                  className="business-dashboard-header__action"
                >
                  {action.icon && <action.icon className="w-5 h-5" />}
                  <span>{action.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>
    );
  }

  /**
   * COMPACT — a title bar rather than a hero card. Opt in with `compact`.
   *
   * The default render below is a full-bleed gradient banner ~120-160px tall, on every page,
   * above the data. That is a marketing-site pattern: Jobber, HouseCall Pro and ServiceTitan
   * all use a compact title bar, because a page's identity comes from the nav highlight and
   * the title, not from a coloured panel. The cost is paid on every screen a contractor
   * opens all day.
   *
   * Chrome is NEUTRAL and the colour moves to the icon, deliberately. Eight per-page
   * gradients are the reason companies.theme_color cannot be honoured here: a contractor
   * sets a brand colour in Settings and sees it on their customer-facing pages
   * (PublicQuoteView, PortalQuote, CustomerScheduling) while the app they use all day
   * ignores it. Accent-not-chrome is what makes branding possible later without letting
   * someone's neon green take over every screen.
   *
   * `stats` IS rendered, as a muted metadata line under the subtitle. The first pass dropped
   * it, reasoning that the tile strip below repeats those numbers — which holds for Jobs,
   * Invoices and Customers and is FALSE elsewhere: Calendar has no tile strip at all, and
   * Documents' "Storage used" appears nowhere else on the page. Dropping them would have
   * removed information from those pages rather than de-duplicating it.
   */
  if (compact) {
    // A bottom rule, not a floating row. Without it the title sits on the same flat
    // background as the content and nothing says where the page chrome ends — the old banner
    // was doing that job with a coloured block. A hairline is the least it can be and still
    // read as a header.
    return (
      <div className={`flex flex-wrap items-center gap-3 pb-4 mb-5 border-b border-gray-200 dark:border-slate-700/60 ${className}`}>
        {Icon && (
          <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${iconSoftClasses[gradient] || iconSoftClasses.blue}`}>
            <Icon className="w-5 h-5" />
          </div>
        )}
        <div className="min-w-0">
          {eyebrow && (
            <p className="text-[11px] font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">{eyebrow}</p>
          )}
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white truncate leading-tight">{title}</h1>
          {subtitle && (
            /* was text-gray-500/dark:text-gray-400 — roughly 4.2:1 on this near-black
               background, under the 4.5:1 AA floor for body text, which is the washed-out
               grey the operator flagged. gray-300 clears it comfortably. */
            <p className="text-sm text-gray-600 dark:text-gray-300 truncate">{subtitle}</p>
          )}

          {/* Stats are RENDERED here, not dropped. The first compact pass omitted them on
              the reasoning that the tile strip below repeats them — true for Jobs, Invoices
              and Customers, and FALSE for others: Calendar has no tile strip at all, and
              Documents' "Storage used" exists nowhere else on the page. Omitting them would
              have deleted information from those pages rather than de-duplicating it.

              A muted metadata line rather than cards: at this size the numbers are context
              for the title, and a page that wants them to be ACTIONABLE has a tile strip for
              that. onClick is still honoured, because some of these do navigate. */}
          {stats.length > 0 && (
            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
              {/* Second copy of the same list, same reasoning as the block above: a stat with
                  no onClick is a number, so it renders as one rather than as a disabled
                  button nobody can focus. */}
              {stats.map((stat, index) => {
                const StatElement = stat.onClick ? 'button' : 'div';
                return (
                  <StatElement
                    key={index}
                    type={stat.onClick ? 'button' : undefined}
                    onClick={stat.onClick}
                    className={`text-xs text-gray-600 dark:text-gray-400 ${stat.onClick ? 'hover:text-blue-600 dark:hover:text-blue-400 underline-offset-2 hover:underline cursor-pointer' : 'cursor-default'}`}
                  >
                    {stat.label}: <span className="font-semibold text-gray-900 dark:text-gray-200">{stat.value}</span>
                  </StatElement>
                );
              })}
            </div>
          )}
        </div>
        {actions.length > 0 && (
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {actions.map((action, index) => (
              <button
                key={index}
                onClick={action.onClick}
                data-testid={action.testId}
                // Board #382: same rule as the full header below. The `primary` flag was only
                // honoured there, and Customers renders THIS branch, so Import (listed first)
                // stayed the solid blue button after the flag was added.
                className={(actions.some((a) => a.primary) ? action.primary : index === 0)
                  ? 'inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 transition-colors'
                  : 'inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-slate-600 px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors'}
              >
                {action.icon && <action.icon className="w-4 h-4" />}
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden rounded-2xl mb-8 ${className}`}>
      {/* Background with gradient */}
      <div className={`absolute inset-0 bg-gradient-to-br ${gradientClasses[gradient]} opacity-90`}></div>
      
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-4 -right-4 w-24 h-24 bg-white opacity-10 rounded-full"></div>
        <div className="absolute top-8 -right-8 w-16 h-16 bg-white opacity-5 rounded-full"></div>
        <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-white opacity-5 rounded-full"></div>
        <div className="absolute bottom-4 left-8 w-8 h-8 bg-white opacity-10 rounded-full"></div>
      </div>

      {/* Content */}
      <div className="relative px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start space-x-3 sm:space-x-4">
            {Icon && (
              <div className={`flex-shrink-0 w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br ${iconBgClasses[gradient]} rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg`}>
                <Icon className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
            )}

            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1 sm:mb-2">
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white truncate">{title}</h1>
                <SparklesIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white opacity-80 flex-shrink-0" />
              </div>

              {subtitle && (
                <p className="text-sm sm:text-base lg:text-lg text-white opacity-90 max-w-2xl line-clamp-2">{subtitle}</p>
              )}

              {/* Quick stats - hide on very small screens, show on sm+ */}
              {stats.length > 0 && (
                <div className="hidden sm:flex flex-wrap items-center gap-3 sm:gap-4 lg:gap-6 mt-3 sm:mt-4">
                  {stats.map((stat, index) => (
                    <div
                      key={index}
                      className={`flex items-center space-x-2 ${stat.onClick ? 'cursor-pointer hover:bg-white hover:bg-opacity-10 px-2 py-1 rounded-lg transition-all duration-200' : ''}`}
                      onClick={stat.onClick}
                    >
                      <div className="w-2 h-2 bg-white rounded-full opacity-60"></div>
                      <span className="text-white opacity-90 text-xs sm:text-sm font-medium">
                        {stat.label}: <span className="font-bold">{stat.value}</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Actions - stack on mobile */}
          {actions.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-4 sm:mt-0">
              {/* The FIRST action is the page's primary one ("Add Vendor", "New Quote")
                  and is now solid. Every action was white text on a white 20% scrim over
                  a coloured gradient — roughly 3.3:1, under the 4.5:1 AA needs at this
                  text size, which is why it read as low-contrast rather than as a button.
                  Solid-white-with-dark-text over a coloured banner is the standard primary
                  treatment (Stripe, Linear, Jobber all use it).

                  Secondary actions keep the glass treatment: they are genuinely secondary,
                  and making every header button solid would flatten the hierarchy and
                  shout. Their contrast is raised to 30%/40% so they clear AA too. */}
              {/* A page can now SAY which action is primary with `primary: true`, instead of
                  it being whichever happened to be written first. Customers listed Import
                  first, so the page's emphasised button was a bulk-data chore rather than
                  Add Customer. Position is unchanged and index 0 is still the default, so
                  every page that has not declared one behaves exactly as before. */}
              {actions.map((action, index) => (
                <button
                  key={index}
                  onClick={action.onClick}
                  data-testid={action.testId}
                  className={`flex items-center justify-center space-x-2 min-w-0 sm:min-w-[120px] h-10 px-3 sm:px-4 py-2 rounded-xl transition-all duration-200 backdrop-blur-sm border hover:scale-105 text-sm sm:text-base ${
                    (actions.some((a) => a.primary) ? action.primary : index === 0)
                      ? 'bg-white text-slate-900 border-transparent shadow-sm hover:bg-slate-100'
                      : 'bg-white bg-opacity-30 hover:bg-opacity-40 text-white border-white border-opacity-40'
                  }`}
                >
                  {action.icon && <action.icon className="w-4 h-4 flex-shrink-0" />}
                  <span className="font-medium whitespace-nowrap">{action.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Bottom decorative line */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-white to-transparent opacity-20"></div>
      </div>
    </div>
  );
};

// Enhanced stat card component
export const ModernStatCard = ({ 
  title, 
  value, 
  change, 
  changeType, 
  icon: Icon, 
  gradient = 'blue',
  onClick,
  loading = false,
  trend = [],
  variant = 'default',
  artSrc,
  artAlt = '',
  // See the note on ModernPageHeader's artFallbackSrc — same purpose, same one-shot
  // guard against a fallback that is itself missing.
  artFallbackSrc = null
}) => {
  const gradientClasses = {
    blue: 'from-blue-500 to-indigo-600',
    green: 'from-green-500 to-emerald-600',
    purple: 'from-purple-500 to-violet-600',
    orange: 'from-orange-500 to-amber-600',
    red: 'from-red-500 to-rose-600',
    indigo: 'from-indigo-500 to-blue-600'
  };

  const handleArtError = (e) => {
    if (!artFallbackSrc || e.target.dataset.fallbackApplied) return;
    e.target.dataset.fallbackApplied = 'true';
    e.target.onerror = null;
    e.target.src = artFallbackSrc;
  };

  // Defensive: ensure icon is a valid React component
  const IconComponent = typeof Icon === 'function' ? Icon : null;

  if (variant === 'business-dashboard') {
    return (
      <div
        className={`business-dashboard-stat business-dashboard-stat--${gradient} ${onClick ? 'is-interactive' : ''}`}
        onClick={onClick}
      >
        <div className="business-dashboard-stat__accent" aria-hidden="true"></div>
        <div className="business-dashboard-stat__content">
          <div className="business-dashboard-stat__copy">
            <h3>{title}</h3>
            {loading ? (
              <div className="business-dashboard-stat__skeleton"></div>
            ) : (
              <p className="business-dashboard-stat__value">{value}</p>
            )}
            {change && (
              <div className={`business-dashboard-stat__change is-${changeType || 'neutral'}`}>
                {changeType === 'increase' ? (
                  <ArrowTrendingUpIcon className="w-4 h-4" />
                ) : (
                  <ChartBarIcon className="w-4 h-4" />
                )}
                <span>{change}</span>
              </div>
            )}
          </div>

          <div className="business-dashboard-stat__visual" aria-hidden={artAlt ? undefined : 'true'}>
            {artSrc ? (
              <img src={artSrc} alt={artAlt} onError={handleArtError} />
            ) : IconComponent ? (
              <IconComponent />
            ) : (
              <SparklesIcon />
            )}
          </div>
        </div>

        {trend.length > 0 && (
          <div className="business-dashboard-stat__trend" aria-hidden="true">
            {trend.map((point, index) => (
              <span
                key={index}
                style={{ height: `${Math.max(10, (point / Math.max(...trend)) * 100)}%` }}
              ></span>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <KPIItem
      label={title}
      value={value}
      icon={IconComponent || SparklesIcon}
      gradient={gradientClasses[gradient] || gradientClasses.blue}
      onClick={onClick}
      loading={loading}
      change={change}
      changeType={changeType}
      trend={trend}
    />
  );
};

// Enhanced action button component
export const ModernActionButton = ({ 
  children, 
  variant = 'primary', 
  size = 'md',
  icon: Icon,
  loading = false,
  className = '',
  ...props 
}) => {
  const variants = {
    primary: 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg shadow-blue-200',
    success: 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-lg shadow-green-200',
    warning: 'bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white shadow-lg shadow-orange-200',
    danger: 'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white shadow-lg shadow-red-200',
    secondary: 'bg-white border-2 border-gray-200 hover:border-gray-300 text-gray-700 hover:bg-gray-50 shadow-lg'
  };

  const sizes = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg'
  };

  return (
    <button
      className={`
        inline-flex items-center justify-center space-x-2 font-semibold rounded-xl
        transition-all duration-200 hover:scale-105 hover:shadow-xl
        disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
        ${variants[variant]} ${sizes[size]} ${className}
      `}
      disabled={loading}
      {...props}
    >
      {loading ? (
        <BoltIcon className="w-5 h-5 animate-spin" />
      ) : Icon ? (
        <Icon className="w-5 h-5" />
      ) : null}
      <span>{children}</span>
    </button>
  );
};

export default ModernPageHeader;
